import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme, THEMES, BACKDROPS } from "@/context/ThemeContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { highlightSwatch } from "@/lib/highlights";
import { Flame, BookOpen, NotebookPen, Bookmark, Trash2, Bell, Share2, Highlighter, Palette } from "lucide-react";
import ShareVerseModal from "@/components/ShareVerseModal";

export default function Profile() {
  const { user, updateUser } = useAuth();
  const { themeId, backdropId, setTheme, setBackdrop } = useTheme();
  const [stats, setStats] = useState(null);
  const [saved, setSaved] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [translation, setTranslation] = useState(user?.translation || "kjv");
  const [reminderOn, setReminderOn] = useState(user?.reminder_enabled || false);
  const [reminderTime, setReminderTime] = useState(user?.reminder_time || "08:00");
  const [shareVerse, setShareVerse] = useState(null);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data));
    api.get("/verses/saved").then((r) => setSaved(r.data));
    api.get("/bible/highlights").then((r) => setHighlights(r.data));
  }, []);

  const removeHighlight = async (h) => {
    await api.delete(`/bible/highlights/${h.id}`);
    setHighlights((hs) => hs.filter((x) => x.id !== h.id));
    toast.success("Highlight removed.");
  };

  const saveAppearance = async (theme, background) => {
    setTheme(theme);
    setBackdrop(background);
    await api.put("/auth/appearance", { theme, background });
    updateUser({ theme, background });
    toast.success("Appearance updated.");
  };

  const saveReminder = async (enabled, time) => {
    if (enabled && typeof Notification !== "undefined" && Notification.permission !== "granted") {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Enable notifications in your browser to receive reminders.");
        enabled = false;
      }
    }
    setReminderOn(enabled);
    setReminderTime(time);
    await api.put("/auth/reminder", { enabled, time });
    updateUser({ reminder_enabled: enabled, reminder_time: time });
    toast.success(enabled ? `Daily reminder set for ${time}.` : "Reminder turned off.");
  };

  const changeTranslation = async (t) => {
    setTranslation(t);
    await api.put("/auth/translation", { translation: t });
    updateUser({ translation: t });
    toast.success("Preference updated.");
  };

  const removeVerse = async (v) => {
    await api.post("/verses/save", v);
    setSaved((s) => s.filter((x) => x.ref !== v.ref));
    toast.success("Verse removed.");
  };

  const cards = [
    { icon: Flame, label: "Current streak", value: stats?.streak ?? 0 },
    { icon: Flame, label: "Longest streak", value: stats?.longest_streak ?? 0 },
    { icon: BookOpen, label: "Practices", value: stats?.practices_completed ?? 0 },
    { icon: NotebookPen, label: "Reflections", value: stats?.journals ?? 0 },
  ];

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-16">
      <div className="animate-fade-up">
        <p className="label-overline mb-2">Your journey</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-none">{user?.name}</h1>
        <p className="text-muted-foreground mt-2 text-sm">{user?.email}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
        {cards.map((c, i) => (
          <div key={c.label} className="bg-card border border-border rounded-md p-6 animate-fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <c.icon className="w-5 h-5 text-accent mb-4" />
            <p className="font-heading text-4xl leading-none">{c.value}</p>
            <p className="text-xs text-muted-foreground mt-2">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="bg-card border border-border rounded-md p-8 mt-6 animate-fade-up">
        <p className="label-overline mb-4">Preferred translation</p>
        <div className="flex gap-3">
          {[{ k: "kjv", n: "King James Version" }, { k: "niv", n: "New International Version" }].map((t) => (
            <button key={t.k} data-testid={`pref-translation-${t.k}`} onClick={() => changeTranslation(t.k)}
              className={`px-5 py-3 rounded-sm border text-sm transition-colors ${translation === t.k ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>
              {t.n}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-card border border-border rounded-md p-8 mt-6 animate-fade-up" data-testid="reminder-settings">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-start gap-3">
            <Bell className="w-5 h-5 text-accent mt-0.5" />
            <div>
              <p className="font-heading text-2xl tracking-tight leading-none">Daily Reminder</p>
              <p className="text-sm text-muted-foreground mt-1.5 max-w-md">A gentle nudge to pause and attune your heart. Notifications appear while Attune is open in your browser.</p>
            </div>
          </div>
          <button data-testid="reminder-toggle" onClick={() => saveReminder(!reminderOn, reminderTime)} role="switch" aria-checked={reminderOn}
            className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${reminderOn ? "bg-primary" : "bg-border"}`}>
            <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-background transition-transform ${reminderOn ? "translate-x-5" : ""}`} />
          </button>
        </div>
        {reminderOn && (
          <div className="flex items-center gap-3 mt-6 pt-6 border-t border-border">
            <label className="text-sm text-muted-foreground">Remind me at</label>
            <input type="time" data-testid="reminder-time-input" value={reminderTime}
              onChange={(e) => saveReminder(true, e.target.value)}
              className="bg-transparent border-b border-border py-1.5 px-1 focus:outline-none focus:border-primary transition-colors" />
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-md p-8 mt-6 animate-fade-up" data-testid="appearance-settings">
        <div className="flex items-start gap-3 mb-7">
          <Palette className="w-5 h-5 text-accent mt-0.5" />
          <div>
            <p className="font-heading text-2xl tracking-tight leading-none">Appearance</p>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-md">Choose a palette and a subtle backdrop for your sanctuary. Your choice follows you across devices.</p>
          </div>
        </div>

        <p className="label-overline mb-3">Theme</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {THEMES.map((t) => (
            <button key={t.id} data-testid={`theme-swatch-${t.id}`} onClick={() => saveAppearance(t.id, backdropId)}
              className={`flex items-center gap-3 border rounded-sm px-4 py-3 text-left transition-colors ${themeId === t.id ? "border-primary bg-muted/60" : "border-border hover:bg-muted/40"}`}>
              <span className="flex -space-x-1.5 shrink-0">
                {t.preview.map((c) => (
                  <span key={c} className="w-4 h-4 rounded-full border border-border" style={{ background: c }} />
                ))}
              </span>
              <span className="text-sm leading-tight">{t.name}</span>
            </button>
          ))}
        </div>

        <p className="label-overline mt-8 mb-3">App backdrop</p>
        <div className="flex items-center gap-2 flex-wrap">
          {BACKDROPS.map((b) => (
            <button key={b.id} data-testid={`backdrop-thumb-${b.id}`} onClick={() => saveAppearance(themeId, b.id)} title={b.name}
              className={`w-16 h-11 rounded-sm overflow-hidden border-2 transition-all ${backdropId === b.id ? "border-accent" : "border-border opacity-75 hover:opacity-100"}`}>
              {b.src
                ? <img src={b.src} alt={b.name} className="w-full h-full object-cover" />
                : <span className="flex items-center justify-center w-full h-full text-[10px] text-muted-foreground bg-muted/40">None</span>}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-2" data-testid="backdrop-label">
          {BACKDROPS.find((b) => b.id === backdropId)?.name}
        </p>
      </div>

      <div className="mt-12">
        <h2 className="font-heading text-3xl tracking-tight flex items-center gap-3"><Bookmark className="w-5 h-5 text-accent" /> Saved Verses</h2>
        <div className="space-y-4 mt-6">
          {saved.length === 0 && <p className="text-muted-foreground text-sm">No saved verses yet. Tap the bookmark on your daily verse to keep it here.</p>}
          {saved.map((v) => (
            <div key={v.ref} className="group bg-card border border-border rounded-md p-6 animate-fade-up" data-testid={`saved-verse-${v.ref.replace(/[^a-z0-9]/gi, "-")}`}>
              <div className="flex items-start justify-between gap-4">
                <blockquote className="font-scripture italic text-xl text-primary leading-relaxed">"{translation === "niv" ? v.niv : v.kjv}"</blockquote>
                <button onClick={() => removeVerse(v)} className="text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100 shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center justify-between mt-3">
                <p className="font-heading text-lg">{v.ref} <span className="text-xs text-muted-foreground font-body ml-2">{v.theme}</span></p>
                <button data-testid={`share-saved-${v.ref.replace(/[^a-z0-9]/gi, "-")}`} onClick={() => setShareVerse(v)} className="text-muted-foreground hover:text-accent transition-colors">
                  <Share2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-12" data-testid="highlights-section">
        <h2 className="font-heading text-3xl tracking-tight flex items-center gap-3"><Highlighter className="w-5 h-5 text-accent" /> Highlights</h2>
        <div className="space-y-4 mt-6">
          {highlights.length === 0 && <p className="text-muted-foreground text-sm">No highlights yet. Tap any verse in the Bible reader to highlight it.</p>}
          {highlights.map((h) => (
            <div key={h.id} className="group bg-card border border-border rounded-md p-6 animate-fade-up" data-testid={`highlight-${h.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <span className="w-3 h-3 rounded-full mt-2 shrink-0" style={{ background: highlightSwatch(h.color) }} />
                  <blockquote className="font-scripture italic text-lg text-primary leading-relaxed">"{h.text}"</blockquote>
                </div>
                <button data-testid={`delete-highlight-${h.id}`} onClick={() => removeHighlight(h)}
                  className="text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100 shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <Link to={`/scripture/${h.slug}/read?chapter=${h.chapter}`} data-testid={`highlight-link-${h.id}`}
                className="inline-block font-heading text-lg mt-3 hover:text-accent transition-colors">
                {h.book} {h.chapter}:{h.verse} →
              </Link>
            </div>
          ))}
        </div>
      </div>
      {shareVerse && <ShareVerseModal verse={shareVerse} translation={translation} onClose={() => setShareVerse(null)} />}
    </div>
  );
}
